import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
    colorSystem,
    DestructiveSecondaryButton,
    Dialog,
    DialogContent,
    DialogTitle,
    Flexbox,
    SearchInput,
    SecondaryButton,
    Switch,
    Text,
    Tooltip,
} from '@luminovo/design-system';
import { Box } from '@mui/material';
import { useSelector } from '@tanstack/react-store';
import { GripVerticalIcon } from 'lucide-react';
import React from 'react';

import { RowData, Table } from '../tanstackTypes';
import { resetTable } from '../utils/utils';
import { getManageableItems, isItemVisible } from './manageableItems';

interface ColumnConfigDialogProps<TData extends RowData> {
    table: Table<TData>;
    open: boolean;
    onClose: () => void;
}

export function ColumnConfigDialog<TData extends RowData>({
    table,
    open,
    onClose,
}: ColumnConfigDialogProps<TData>): JSX.Element {
    const [searchQuery, setSearchQuery] = React.useState('');
    const columnOrder = useSelector(table.store, (state) => state.columnOrder);
    const manageableItems = React.useMemo(() => getManageableItems(table), [table]);
    const leafColumns = table.getAllLeafColumns().filter((col) => col.getCanHide());
    const { enableColumnHiding } = table.options.meta || {};

    const orderedItemIds = React.useMemo(() => {
        const leafOrder = columnOrder.length > 0 ? columnOrder : leafColumns.map(({ id }) => id);

        const seenItems = new Set<string>();
        const ordered: string[] = [];
        for (const leafId of leafOrder) {
            const item = manageableItems.find((m) => m.leafIds.includes(leafId));
            if (item && !seenItems.has(item.id)) {
                seenItems.add(item.id);
                ordered.push(item.id);
            }
        }
        for (const item of manageableItems) {
            if (!seenItems.has(item.id)) {
                ordered.push(item.id);
            }
        }
        return ordered;
    }, [columnOrder, leafColumns, manageableItems]);

    const filteredItemIds = React.useMemo(() => {
        if (!searchQuery.trim()) {
            return orderedItemIds;
        }
        const lowerQuery = searchQuery.toLowerCase();
        return orderedItemIds.filter((itemId) => {
            const item = manageableItems.find((m) => m.id === itemId);
            if (!item) return false;
            return item.label.toLowerCase().includes(lowerQuery);
        });
    }, [orderedItemIds, searchQuery, manageableItems]);

    const expandItemsToLeafOrder = React.useCallback(
        (itemIds: string[]): string[] => {
            const leafOrder: string[] = [];
            for (const itemId of itemIds) {
                const item = manageableItems.find((m) => m.id === itemId);
                if (item) {
                    leafOrder.push(...item.leafIds);
                }
            }
            return leafOrder;
        },
        [manageableItems],
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = orderedItemIds.indexOf(active.id as string);
        const newIndex = orderedItemIds.indexOf(over.id as string);
        if (oldIndex === newIndex) return;

        const newOrder = [...orderedItemIds];
        const [moved] = newOrder.splice(oldIndex, 1);
        newOrder.splice(newIndex, 0, moved);
        table.setColumnOrder(expandItemsToLeafOrder(newOrder));
    };

    const handleToggleVisibility = (itemId: string) => {
        const item = manageableItems.find((m) => m.id === itemId);
        if (!item) return;
        const visible = isItemVisible(item, table);
        item.leafIds.forEach((leafId) => {
            const col = table.getColumn(leafId);
            if (col) col.toggleVisibility(!visible);
        });
    };

    const handleEnableAll = () => {
        for (const item of manageableItems) {
            item.leafIds.forEach((leafId) => {
                const col = table.getColumn(leafId);
                if (col && !col.getIsVisible()) col.toggleVisibility(true);
            });
        }
    };

    const handleReset = () => {
        resetTable(table);
    };

    const visibleCount = manageableItems.filter((item) => isItemVisible(item, table)).length;
    const totalCount = manageableItems.length;
    const hasOnlyOneVisible = visibleCount === 1;
    const hasAllVisible = visibleCount === totalCount;

    return (
        <Dialog
            maxWidth={false}
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        width: '420px',
                        height: '75vh',
                        maxHeight: '800px',
                        minHeight: '400px',
                    },
                },
            }}
        >
            <DialogTitle title={t`Manage columns`} onClose={onClose} />
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', padding: 0, height: '100%' }}>
                <Box
                    sx={{
                        padding: '8px 12px',
                        borderBottom: `1px solid ${colorSystem.neutral[2]}`,
                        flexShrink: 0,
                        backgroundColor: colorSystem.neutral.white,
                    }}
                >
                    <SearchInput
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder={t`Search columns...`}
                        autoFocus={true}
                        style={{ width: '100%' }}
                    />
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto', padding: '8px 12px' }}>
                    <Flexbox
                        gap={2}
                        sx={{
                            flexDirection: 'column',
                        }}
                    >
                        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={filteredItemIds} strategy={verticalListSortingStrategy}>
                                {filteredItemIds.map((itemId) => {
                                    const item = manageableItems.find((m) => m.id === itemId);
                                    if (!item) return null;

                                    const visible = isItemVisible(item, table);
                                    const isOnlyVisible = visible && hasOnlyOneVisible;

                                    return (
                                        <ColumnConfigItem
                                            key={itemId}
                                            columnId={itemId}
                                            label={item.label}
                                            isVisible={visible}
                                            onToggle={() => handleToggleVisibility(itemId)}
                                            disabled={!enableColumnHiding || isOnlyVisible}
                                        />
                                    );
                                })}
                            </SortableContext>
                        </DndContext>

                        {filteredItemIds.length === 0 && (
                            <Flexbox
                                style={{ padding: '24px' }}
                                sx={{
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <Text variant="body-small" style={{ color: colorSystem.neutral[6] }}>
                                    <Trans>No columns found</Trans>
                                </Text>
                            </Flexbox>
                        )}
                    </Flexbox>
                </Box>

                <Box
                    sx={{
                        padding: '12px',
                        borderTop: `1px solid ${colorSystem.neutral[2]}`,
                        flexShrink: 0,
                        backgroundColor: colorSystem.neutral.white,
                    }}
                >
                    <Flexbox
                        sx={{
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Tooltip title={t`Reset columns to default configuration`}>
                            <DestructiveSecondaryButton size="small" onClick={handleReset}>
                                <Trans>Reset</Trans>
                            </DestructiveSecondaryButton>
                        </Tooltip>
                        <Flexbox
                            gap={12}
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <Text variant="body-small" style={{ color: colorSystem.neutral[6] }}>
                                {visibleCount} <Trans>of</Trans> {totalCount}
                            </Text>
                            <Tooltip title={t`Show all columns`}>
                                <SecondaryButton size="small" onClick={handleEnableAll} disabled={hasAllVisible}>
                                    <Trans>Show all</Trans>
                                </SecondaryButton>
                            </Tooltip>
                        </Flexbox>
                    </Flexbox>
                </Box>
            </DialogContent>
        </Dialog>
    );
}

interface ColumnConfigItemProps {
    columnId: string;
    label: string;
    isVisible: boolean;
    onToggle: () => void;
    disabled: boolean;
}

function ColumnConfigItem({ columnId, label, isVisible, onToggle, disabled }: ColumnConfigItemProps): JSX.Element {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: columnId,
        disabled,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <Box
            ref={setNodeRef}
            style={style}
            onClick={disabled ? undefined : onToggle}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 8px',
                backgroundColor: colorSystem.neutral.white,
                borderRadius: '4px',
                cursor: disabled ? 'default' : 'pointer',
                '&:hover': {
                    backgroundColor: disabled ? colorSystem.neutral.white : colorSystem.neutral[1],
                },
            }}
        >
            <Box
                {...listeners}
                {...attributes}
                onClick={(e) => e.stopPropagation()}
                sx={{
                    cursor: disabled ? 'default' : 'grab',
                    display: 'flex',
                    alignItems: 'center',
                    color: disabled ? colorSystem.neutral[4] : colorSystem.neutral[5],
                    flexShrink: 0,
                    '&:active': {
                        cursor: disabled ? 'default' : 'grabbing',
                    },
                }}
            >
                <GripVerticalIcon size={16} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Text variant="body" showEllipsis={true}>
                    {label}
                </Text>
            </Box>

            <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
                <Switch checked={isVisible} onChange={onToggle} disabled={disabled} size="small" />
            </Box>
        </Box>
    );
}
